import { Col, Form, Row, Input, Spin, Upload, notification, Select, DatePicker, Modal, Switch } from 'antd';
import React, { useEffect, useState } from 'react';
import { TYPE_PAGE } from '../../constants';
import './DriverForm.scss';
import OptionalLabel from '../Elements/OptionalLabel';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const initValuesForm = {
  email: '',
  password: '',
};

const currentYear = new Date().getFullYear();
const years = Array.from(new Array(30), (val, index) => currentYear - index + 10);

const DriverForm = ({
  handleOk,
  driver,
  formDriver,
  isFetching,
  fileList,
  setFileList,
  typePage = TYPE_PAGE.NEW,
  vehicles,
  types,
  vehicleInspectionDocuments,
  setVehicleInspectionDocuments,
  vehicleInsurancePhotos,
  setVehicleInsurancePhotos,
  driverLicensePhotos,
  setDriverLicensePhotos,
  driverAbstracts,
  setDriverAbstracts,
}) => {
  const [api, contextHolder] = notification.useNotification();
  const { Option } = Select;

  const [selectedMake, setSelectedMake] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');

  useEffect(() => {
    if (driver) {
      formDriver.setFieldsValue({
        ...driver,
        vehicleInsuranceExpiryDate: driver.vehicleInsuranceExpiryDate
          ? dayjs(driver.vehicleInsuranceExpiryDate, 'YYYY-MM-DD')
          : null,
        vehicleInspectionExpiryDate: driver.vehicleInspectionExpiryDate
          ? dayjs(driver.vehicleInspectionExpiryDate, 'YYYY-MM-DD')
          : null,
        driverLicenseExpiryDate: driver.driverLicenseExpiryDate
          ? dayjs(driver.driverLicenseExpiryDate, 'YYYY-MM-DD')
          : null,
      });
    } else {
      formDriver.resetFields();
    }
  }, [formDriver, driver]);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return fileList;
  };

  const handleChange = (info, type) => {
    let newFileList = [...info.fileList];
    const file = newFileList.slice(-1)[0];
    const isLt5M = (file?.size || 0) / 1024 / 1024 <= 5;
    if (!isLt5M) {
      api.error({ message: 'Image must smaller than 5MB!' });
    } else {
      newFileList = newFileList.map((f) => {
        if (!f.url && !f.thumbUrl && f.originFileObj) {
          const isPdf = f.name?.toLowerCase().endsWith('.pdf') || f.type === 'application/pdf';
          if (isPdf) {
            f.thumbUrl = '/icons/pdf.svg';
          } else {
            f.thumbUrl = URL.createObjectURL(f.originFileObj);
          }
        }
        return f;
      });

      newFileList = newFileList.slice(-1);
      if (type === 'vehiclePhotos') {
        setFileList(newFileList);
      } else if (type === 'vehicleInspectionDocuments') {
        setVehicleInspectionDocuments(newFileList);
      } else if (type === 'vehicleInsurancePhotos') {
        setVehicleInsurancePhotos(newFileList);
      } else if (type === 'driverLicensePhotos') {
        setDriverLicensePhotos(newFileList);
      } else if (type === 'driverAbstracts') {
        setDriverAbstracts(newFileList);
      }
    }
  };

  const handlePreviewFile = async (file) => {
    let preview = file.url || file.preview;
    if (!preview && file.originFileObj) {
      preview = URL.createObjectURL(file.originFileObj);
    }

    const fileUrl = preview || file?.thumbUrl || file?.response?.url;
    if (fileUrl) {
      const isPdf =
        file.name?.toLowerCase().endsWith('.pdf') ||
        fileUrl?.toLowerCase().includes('.pdf') ||
        file.response?.key?.toLowerCase().endsWith('.pdf');

      if (isPdf) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      } else {
        setPreviewImage(fileUrl);
        setPreviewOpen(true);
        setPreviewTitle(file.name || (file.url && file.url.substring(file.url.lastIndexOf('/') + 1)) || 'Preview');
      }
    }
  };

  const handleDownloadFile = (file) => {
    const fileUrl = file?.url || file?.thumbUrl || file?.response?.url;
    if (!fileUrl) return;

    const link = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = file?.name || 'document';
    link.click();
  };

  const uploadListOptions = {
    showPreviewIcon: true,
    showDownloadIcon: true,
    showRemoveIcon: false,
  };

  const renderUploadItem = (originNode, file, _fileItems, actions) => {
    if (!file) return originNode;
    return (
      <div className="custom-upload-item-wrapper">
        {originNode}
        {actions?.remove && (
          <button
            type="button"
            className="custom-upload-remove-btn"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              actions.remove();
            }}
          >
            <CloseOutlined />
          </button>
        )}
      </div>
    );
  };

  const onFinishForm = (values) => {
    const params = { ...values, password: undefined };
    if (values.password) {
      params.password = values.password;
    }
    if (values.vehicleYear) {
      params.vehicleYear = String(values.vehicleYear);
    }
    if (values.vehicleInsuranceExpiryDate) {
      const d = dayjs(values.vehicleInsuranceExpiryDate);
      params.vehicleInsuranceExpiryDate = d.isValid() ? d.format('YYYY-MM-DD') : values.vehicleInsuranceExpiryDate;
    }
    if (values.vehicleInspectionExpiryDate) {
      const d = dayjs(values.vehicleInspectionExpiryDate);
      params.vehicleInspectionExpiryDate = d.isValid() ? d.format('YYYY-MM-DD') : values.vehicleInspectionExpiryDate;
    }
    if (values.driverLicenseExpiryDate) {
      const d = dayjs(values.driverLicenseExpiryDate);
      params.driverLicenseExpiryDate = d.isValid() ? d.format('YYYY-MM-DD') : values.driverLicenseExpiryDate;
    }
    handleOk && handleOk(params);
  };

  const handleMakeChange = (value) => {
    setSelectedMake(value);
    const selectedCar = vehicles.find((car) => car.make === value);
    setAvailableModels(selectedCar ? selectedCar.models : []);
  };

  return (
    <Spin spinning={isFetching}>
      {contextHolder}
      <div className="DriverForm">
        <Form
          form={formDriver}
          scrollToFirstError
          layout="vertical"
          requiredMark={false}
          colon={false}
          onFinish={onFinishForm}
          initialValues={initValuesForm}
        >
          <Row gutter={16}>
            {formDriver.getFieldValue('internalId') && (
              <Col span={24}>
                <Form.Item label="Driver ID" name="internalId">
                  <Input size="large" disabled />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: 'Required' }]}>
                <Input size="large" placeholder="First Name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: 'Required' }]}>
                <Input size="large" placeholder="Last Name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Phone" name="phone" rules={[{ required: true, message: 'Required' }]}>
                <Input size="large" placeholder="Phone" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, type: 'email', message: 'The input is not valid E-mail!' }]}
              >
                <Input
                  disabled={typePage === TYPE_PAGE.EDIT}
                  size="large"
                  placeholder="Email"
                  autoComplete="new-password"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: !driver, message: 'Required' },
                  { min: 6, message: 'Password must be minimum 6 characters.' },
                ]}
              >
                <Input.Password size="large" autoComplete="new-password" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label={<OptionalLabel title="GST Number" />} name="gstNumber">
                <Input size="large" placeholder="GST Number" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="GST Payout (add 5% GST)"
                name="gstPayoutEnabled"
                valuePropName="checked"
                extra="Only applies when the driver has a GST Number."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Preferred Driver" name="isPreferred" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Vehicle Make" name={'vehicleMake'}>
                <Select placeholder="Select a vehicle make" size="large" onChange={handleMakeChange}>
                  {vehicles.map((car) => (
                    <Option key={car.make} value={car.make}>
                      {car.make}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Vehicle Type" name={'vehicleType'}>
                <Select placeholder="Select a vehicle type" size="large">
                  {types.map((type) => (
                    <Option key={type.type} value={type.type}>
                      {type.displayName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Vehicle Model" name="vehicleModel">
                <Select
                  placeholder={selectedMake ? 'Select a vehicle model' : 'Select a make first'}
                  size="large"
                  disabled={!selectedMake}
                >
                  {availableModels.map((model) => (
                    <Option key={model} value={model}>
                      {model}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Vehicle Color" name="vehicleColor">
                <Input size="large" placeholder="Vehicle Color" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Vehicle License Plate Number" name="vehicleIPNo">
                <Input size="large" placeholder="Vehicle License Plate Number" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Vehicle Year" name="vehicleYear">
                <Select placeholder="Select a year" size="large">
                  {years.map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Vehicle Photo" />}
                name="vehiclePhotos"
                getValueFromEvent={normFile}
              >
                <Upload.Dragger
                  multiple={false}
                  maxCount={1}
                  accept=".pdf,.heic,.heif,.pdfx,.odt,.doc,.docx,.png,.jpg,.jpeg"
                  showUploadList={uploadListOptions}
                  itemRender={renderUploadItem}
                  onPreview={handlePreviewFile}
                  onDownload={handleDownloadFile}
                  customRequest={async ({ onSuccess, onError }) => {
                    try {
                      onSuccess('ok');
                    } catch (e) {
                      console.log(e);
                      onError();
                    }
                  }}
                  onRemove={() => setFileList([])}
                  listType="picture-card"
                  fileList={fileList}
                  onChange={(info) => handleChange(info, 'vehiclePhotos')}
                >
                  <p className="upload-text">Drag and drop to upload or</p>
                  <p className="upload-drag-icon">
                    Browse
                    <UploadOutlined />
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Vehicle Insurance Photo" />}
                name="vehicleInsurancePhotos"
                getValueFromEvent={normFile}
              >
                <Upload.Dragger
                  multiple={false}
                  maxCount={1}
                  accept=".pdf,.heic,.heif,.pdfx,.odt,.doc,.docx,.png,.jpg,.jpeg"
                  showUploadList={uploadListOptions}
                  itemRender={renderUploadItem}
                  onPreview={handlePreviewFile}
                  onDownload={handleDownloadFile}
                  customRequest={async ({ onSuccess, onError }) => {
                    try {
                      onSuccess('ok');
                    } catch (e) {
                      console.log(e);
                      onError();
                    }
                  }}
                  onRemove={() => setVehicleInsurancePhotos([])}
                  listType="picture-card"
                  fileList={vehicleInsurancePhotos}
                  onChange={(info) => handleChange(info, 'vehicleInsurancePhotos')}
                >
                  <p className="upload-text">Drag and drop to upload or</p>
                  <p className="upload-drag-icon">
                    Browse
                    <UploadOutlined />
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Vehicle Insurance Photo Expired Date" />}
                name="vehicleInsuranceExpiryDate"
                getValueProps={(value) => ({
                  value: value ? dayjs(value, 'YYYY-MM-DD') : null,
                })}
              >
                <DatePicker size="large" style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Driver License" />}
                name="driverLicensePhotos"
                getValueFromEvent={normFile}
              >
                <Upload.Dragger
                  multiple={false}
                  maxCount={1}
                  accept=".pdf,.heic,.heif,.pdfx,.odt,.doc,.docx,.png,.jpg,.jpeg"
                  showUploadList={uploadListOptions}
                  itemRender={renderUploadItem}
                  onPreview={handlePreviewFile}
                  onDownload={handleDownloadFile}
                  customRequest={async ({ onSuccess, onError }) => {
                    try {
                      onSuccess('ok');
                    } catch (e) {
                      console.log(e);
                      onError();
                    }
                  }}
                  onRemove={() => setDriverLicensePhotos([])}
                  listType="picture-card"
                  fileList={driverLicensePhotos}
                  onChange={(info) => handleChange(info, 'driverLicensePhotos')}
                >
                  <p className="upload-text">Drag and drop to upload or</p>
                  <p className="upload-drag-icon">
                    Browse
                    <UploadOutlined />
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Driver License Expired Date" />}
                name="driverLicenseExpiryDate"
                getValueProps={(value) => ({
                  value: value ? dayjs(value, 'YYYY-MM-DD') : null,
                })}
              >
                <DatePicker size="large" style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Driver Abstract" />}
                name="driverAbstracts"
                getValueFromEvent={normFile}
              >
                <Upload.Dragger
                  multiple={false}
                  maxCount={1}
                  accept=".pdf,.heic,.heif,.pdfx,.odt,.doc,.docx,.png,.jpg,.jpeg"
                  showUploadList={uploadListOptions}
                  itemRender={renderUploadItem}
                  onPreview={handlePreviewFile}
                  onDownload={handleDownloadFile}
                  customRequest={async ({ onSuccess, onError }) => {
                    try {
                      onSuccess('ok');
                    } catch (e) {
                      console.log(e);
                      onError();
                    }
                  }}
                  onRemove={() => setDriverAbstracts([])}
                  listType="picture-card"
                  fileList={driverAbstracts}
                  onChange={(info) => handleChange(info, 'driverAbstracts')}
                >
                  <p className="upload-text">Drag and drop to upload or</p>
                  <p className="upload-drag-icon">
                    Browse
                    <UploadOutlined />
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Vehicle Inspection Document" />}
                name="vehicleInspectionDocuments"
                getValueFromEvent={normFile}
              >
                <Upload.Dragger
                  multiple={false}
                  maxCount={1}
                  accept=".pdf,.heic,.heif,.pdfx,.odt,.doc,.docx,.png,.jpg,.jpeg"
                  showUploadList={uploadListOptions}
                  itemRender={renderUploadItem}
                  onPreview={handlePreviewFile}
                  onDownload={handleDownloadFile}
                  customRequest={async ({ onSuccess, onError }) => {
                    try {
                      onSuccess('ok');
                    } catch (e) {
                      console.log(e);
                      onError();
                    }
                  }}
                  onRemove={() => setVehicleInspectionDocuments([])}
                  listType="picture-card"
                  fileList={vehicleInspectionDocuments}
                  onChange={(info) => handleChange(info, 'vehicleInspectionDocuments')}
                >
                  <p className="upload-text">Drag and drop to upload or</p>
                  <p className="upload-drag-icon">
                    Browse
                    <UploadOutlined />
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={<OptionalLabel title="Vehicle Inspection Document Expired Date" />}
                name="vehicleInspectionExpiryDate"
                getValueProps={(value) => ({
                  value: value ? dayjs(value, 'YYYY-MM-DD') : null,
                })}
              >
                <DatePicker size="large" style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </div>
    </Spin>
  );
};

export default DriverForm;
